
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import { SNACKBAR_STATUS } from '@/constants/constants';
import {
	DEFAULT_SHOW_TEXT_COUNTER_AT,
	DEFAULT_TEXT_MAX_LENGTH,
	getTextMaxLengthRule,
} from '@/utils';
import BackButton from '@/components/base/BackButton.vue';
import { db } from '@/firebase';
import { Announcement, AnnouncementType } from '@run-diffusion/shared';
import _trim from 'lodash/trim';
import ActionsIsland from '@/components/ActionsIsland.vue';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import DatePicker from '@/components/base/DatePicker.vue';
import BaseToggle from '@/components/base/BaseToggle.vue';
import TimePicker from '@/components/base/TimePicker.vue';
import { ROUTER } from '@/router/constants';
import AnnouncementPreview from './AnnouncementPreview.vue';
import { get$bindFirestoreOptions } from '@/mixins';
import moment from 'moment-timezone';
import { convertMomentToDateTimeInputValues, combineDateAndTime } from '@/utils';

export default Vue.extend({
	name: 'AdminEditAnnouncements',
	metaInfo: {
		title: 'Edit Announcement Banners | Admin | RunDiffusion',
	},
	data () {
		return {
			DEFAULT_SHOW_TEXT_COUNTER_AT,
			DEFAULT_TEXT_MAX_LENGTH,
			ANNOUNCEMENT_TYPES: [
				'ALERT',
				'WARNING',
				'INFO',
				'NEWS',
				'NO_NEW_SESSIONS'
			] as AnnouncementType[],

			createNewAnnouncementMessage: '',
			announcements: [] as Announcement[],
			editAnnouncement: null as Announcement,
			expandedAnnouncement: null as Announcement,
			localExpirationDate: null as string,
			localExpirationTimeInMinutes: null as number,

			isCreatingNew: false,
			duplicateAnnouncement: null as Announcement,

			saving: false,
			loading: false,
			formValid: false,
			rules: {
				requiredTextField: [
					getTextMaxLengthRule(),
					v => !!_trim(v) || 'Required',
				],
				required: [
					v => !!_trim(v) || 'Required',
				],
				optionalTextField: [
					getTextMaxLengthRule()
				],
			},
		};
	},
	computed: {
		...mapState([
			'user',
		])
	},
	watch: {
		editAnnouncement: {
			immediate: true,
			handler(announcement: Announcement) {
				if (announcement && announcement.expiresAt) {
					const { dateStr, timeInMinutes } = convertMomentToDateTimeInputValues(moment(announcement.expiresAt.toMillis()));
					this.localExpirationDate = dateStr;
					this.localExpirationTimeInMinutes = timeInMinutes;
				} else {
					this.localExpirationDate = null;
					this.localExpirationTimeInMinutes = null;
				}
			}
		}
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		_trim,
		isExpired(announcement: Announcement) {
			return !!(
				announcement.expiresAt &&
				announcement.expiresAt.toDate() < new Date()
			);
		},
		isAnnouncementVisible(announcement: Announcement) {
			return !!(announcement.show && !this.isExpired(announcement));
		},
		setLocalExpirationDate(isoDateString: string) {
			this.localExpirationDate = isoDateString;
			this.updateExpirationTimestamp();
		},
		setLocalExpirationTimeInMinutes(timeInMinutes: number) {
			this.localExpirationTimeInMinutes = timeInMinutes;
			this.updateExpirationTimestamp();
		},
		updateExpirationTimestamp() {
			if (!this.localExpirationDate) {
				this.editAnnouncement.expiresAt = null;
				return;
			}

			const expirationDate = combineDateAndTime(
				this.localExpirationDate,
				this.localExpirationTimeInMinutes || 0
			);
			this.editAnnouncement.expiresAt = expirationDate;
		},
		goBack () {
			if (this.$route.query.back) {
				this.routerPush(this.$route, this.$router, { path: this.$route.query.back as string });
			} else {
				this.routerPush(this.$route, this.$router, { name: ROUTER.SESSIONS });
			}
		},
		onCreateNewClick (announcement?: Announcement) {
			this.isCreatingNew = true;
			this.duplicateAnnouncement = announcement || null;
			this.createNewAnnouncementMessage = announcement ? announcement.message : '';
		},
		onEditAnnouncementCancel () {
			this.editAnnouncement = null;
			this.localExpirationDate = null;
			this.localExpirationTimeInMinutes = null;
		},
		async bindAnnouncements() {
			this.loading = true;
			try {
				await this.$bind(
					'announcements',
					db.collection('announcements').orderBy('expiresAt', 'desc'),
					get$bindFirestoreOptions(),
				);
			} catch (err) {
				console.error('Error binding announcements:', err);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error loading announcements',
					show: true,
				});
			} finally {
				this.loading = false;
			}
		},
		async onCreateNew (closeCallback: Function) {
			const message: string = _trim(this.createNewAnnouncementMessage);
			if (this.saving || !message) return;

			try {
				this.saving = true;

				// Generate a unique ID
				const announcementRef = db.collection('announcements').doc();

				await announcementRef.set({
					createdAt: new Date(),
					message,
					show: false,
					allowAdmins: false,
					type: 'INFO' as AnnouncementType,
					url: '',
					urlBlankTarget: false,
					...(this.duplicateAnnouncement ? {
						allowAdmins: this.duplicateAnnouncement.allowAdmins,
						type: this.duplicateAnnouncement.type,
						expiresAt: this.duplicateAnnouncement.expiresAt,
						url: this.duplicateAnnouncement.url,
						urlBlankTarget: this.duplicateAnnouncement.urlBlankTarget,
					} : {})
				});

				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: `Success! Announcement created.`,
					show: true,
				});

				this.createNewAnnouncementMessage = '';
				this.duplicateAnnouncement = null;
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error creating announcement`,
					show: true,
				});
			} finally {
				if (closeCallback) closeCallback();
				this.saving = false;
			}
		},
		async onSave () {
			if (!this.editAnnouncement || this.saving) return;
			if (!this.$refs.form.validate()) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Form invalid, check fields',
					show: true,
				});
				return;
			}

			try {
				this.saving = true;

				// If creating a new announcement
				let announcementRef;
				if (!this.editAnnouncement.id) {
					announcementRef = db.collection('announcements').doc();
				} else {
					announcementRef = db.doc(`announcements/${this.editAnnouncement.id}`);
				}

				await announcementRef.set({
					...(!this.editAnnouncement.id ? { createdAt: new Date() } : {}),
					message: _trim(this.editAnnouncement.message),
					show: !!this.editAnnouncement.show,
					allowAdmins: !!this.editAnnouncement.allowAdmins,
					type: this.editAnnouncement.type,
					expiresAt: this.editAnnouncement.expiresAt,
					url: _trim(this.editAnnouncement.url || ''),
					urlBlankTarget: !!this.editAnnouncement.urlBlankTarget,
				}, { merge: true });

				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: `Success! Announcement saved.`,
					show: true,
				});

				this.editAnnouncement = null;
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error saving announcement`,
					show: true,
				});
			} finally {
				this.saving = false;
			}
		},
	},
	created() {
		this.bindAnnouncements();
	},
	components: {
		AnnouncementPreview,
		ConfirmDialog,
		ActionsIsland,
		BackButton,
		DatePicker,
		BaseToggle,
		TimePicker,
	},
});
